import React, { useEffect, useState } from "react";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { Sidebar, BaseButton } from "@velocity-ehs/component-library";
import Tree, {
  formatDataForTree,
  setChildrenAtNode,
  TreeNode,
} from "../../sharedComponents/tree/PrimeTree";
import { getOrganizationalUnits } from "../../supportFunctions/graphql/HierarchiesQueries";
import { Hierarchy, OrgUnit } from "./Hierarchies";
import classes from "./MoveOrgUnitSlider.module.scss";
import { TreeExpandedEvent } from "primereact/tree";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import GatewayService from "../../../services/GatewayService";

interface Props {
  showSlider: boolean;
  selectedOrg?: OrgUnit;
  selectedHierarchy?: Hierarchy;
  closeSliderCallback: () => void;
  moveOrgUnit: (destinationId: string, name: string) => void;
}

const MoveOrgUnitSlider: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbarActionContext();
  const [nodes, setNodes] = useState<TreeNode[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [destinationOrg, setDestinationOrg] = useState<TreeNode | undefined>(
    undefined
  );

  const handleAddOrgUnit = async () => {
    if (!destinationOrg) return;
    setIsSaving(true);
    await props.moveOrgUnit(destinationOrg.id, destinationOrg.label);
    setIsSaving(false);
  };

  async function getOrgUnits(filter: any, path?: string) {
    setIsLoading(true);
    try {
      const results = await GatewayService.api.post(
        "",
        getOrganizationalUnits(filter)
      );
      const nodes = results.data.data.organizationalUnits.items;
      return formatDataForTree(nodes, path);
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsLoading(false);
    }
  }

  const getRootOrg = async (id: string) => {
    const filter = {
      and: {
        customerId: UsersService.customer.id,
        id,
      },
    };

    const nodes = await getOrgUnits(filter);
    if (!nodes) return;
    getExpandedOrgUnits(nodes, nodes[0]);
    setExpandedKeys(nodes[0].key);
  };

  const getExpandedOrgUnits = async (nodes: TreeNode[], parent: TreeNode) => {
    const disableAt = props.selectedOrg ? [props.selectedOrg.id] : undefined;
    const filter = {
      and: {
        customerId: UsersService.customer.id,
        parentOrganizationalUnitId: parent.id,
      },
    };

    const children = await getOrgUnits(filter, parent.key);
    if (!children) return;
    const fullTree = setChildrenAtNode(nodes, children, parent.key, disableAt);
    setNodes(fullTree);
  };

  const handleRowClick = (event: any) => {
    setDestinationOrg(event.node);
  };

  const handleExpanderClick = async (event: any) => {
    if (!nodes || event.node.children) return;
    getExpandedOrgUnits(nodes, event.node);
  };

  const handleOnToggle = (event: TreeExpandedEvent) => {
    setExpandedKeys(event.value);
  };

  const footerButtons = () => {
    return (
      <BaseButton
        loading={isSaving}
        onClick={handleAddOrgUnit}
        label={t("action_move")}
      />
    );
  };

  const content = (
    <div className={classes.content}>
      <div className={classes.sourceOrg}>
        <h3>{t("action_moving")}</h3>
        <p>{props.selectedOrg?.name}</p>
        <p className={classes.lineage}>{props.selectedOrg?.lineage}</p>
      </div>
      <Tree
        expandedKeys={expandedKeys}
        nodes={nodes as TreeNode[]}
        selectionKeys={destinationOrg?.key}
        onSelect={handleRowClick}
        onExpand={handleExpanderClick}
        onToggle={handleOnToggle}
        loading={isLoading}
      />
    </div>
  );

  useEffect(() => {
    if (props.showSlider && props.selectedHierarchy) {
      getRootOrg(props.selectedHierarchy.topOrgUnit.id);
    }

    if (!props.showSlider) setDestinationOrg(undefined);
    // eslint-disable-next-line
  }, [props.showSlider, props.selectedHierarchy]);

  return (
    <Sidebar
      header={t("action_move_org_unit")}
      visible={props.showSlider}
      onHide={props.closeSliderCallback}
      footer={footerButtons()}
    >
      {content}
    </Sidebar>
  );
};

export default MoveOrgUnitSlider;
